import {useState} from 'react';

import {useCallbackWithRef} from '@pb/kit-util-use-callback-with-ref';

/**
 * A drop-in replacement for `useState` that supports both controlled and
 * uncontrolled usage patterns from the same hook call.
 *
 * Implements the controlled/uncontrolled duality used by most form components:
 * - **Uncontrolled** (no arguments): state is managed internally, behaves
 *   exactly like `useState`.
 * - **Callback-only** (`externalSetter` provided, no `externalValue`): state
 *   is still managed internally, but the external setter is called on every
 *   update — useful for side effects or lifting state lazily.
 * - **Fully controlled** (both `externalValue` and `externalSetter`): state is
 *   driven entirely by the caller; the internal state is kept in sync but the
 *   external value always wins.
 *
 * The returned setter always calls both the internal `useState` dispatcher
 * and `externalSetter` (if provided), keeping both sides in sync.
 *
 * @template S - Type of the state value.
 * @template T - Type of the setter function (must accept `S` as its first argument).
 * @param externalValue - The controlled value from props. When `undefined`,
 *   the hook falls back to internal state.
 * @param externalSetter - Optional callback called whenever the state changes.
 *   Receives the same arguments as the returned setter.
 * @param initialInternalValue - Initial value for internal state. Used only
 *   when `externalValue` is `undefined` on the first render.
 * @returns A `[value, setter]` tuple analogous to `useState`.
 *
 * @example
 * ```tsx
 * // Uncontrolled
 * const [open, setOpen] = useControlledState(false);
 *
 * // Controlled
 * const [open, setOpen] = useControlledState(props.open, props.onOpenChange, false);
 * ```
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const useControlledState = <S = unknown, T extends ((arg0: S, ...argN: any[]) => void) = ((arg0: S) => void)>(
    externalValue?: S,
    externalSetter?: T,
    initialInternalValue?: S,
): [S, T] => {
    const [internalValue, internalSetter] = useState<S>((externalValue ?? initialInternalValue) as S);
    const setter = useCallbackWithRef(((...args) => {
        internalSetter(args[0]);
        externalSetter?.(...args);
    }) as T);
    return [(externalValue === undefined ? internalValue : externalValue) as S, setter];
};
